import Air from "@mui/icons-material/Air";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Waves from "@mui/icons-material/Waves";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { alpha, Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  HarmonizedViolationProgram,
  HarmonizedViolationResolutionStatus,
  HarmonizedViolationSeverity,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { ReactNode } from "react";

/**
 * A row is stale when EPA stopped reporting it: its lastSeenAt predates the
 * start of the most recent successful refresh. Compared against the refresh
 * START (not finish) — a row touched mid-run has lastSeenAt >= startedAt, so
 * finishedAt would false-flag it. No refresh to compare against → never stale.
 */
export function isViolationStale(
  lastSeenAt: string,
  refreshStartedAt: string | null | undefined
): boolean {
  if (!refreshStartedAt) return false;
  return new Date(lastSeenAt).getTime() < new Date(refreshStartedAt).getTime();
}

export function formatViolationDate(value: string | null | undefined): string {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : "—";
}

/** A date we can actually print — anything else has to drop out of a sentence. */
function isUsableDate(value: string | null | undefined): value is string {
  return Boolean(value) && DateTime.fromISO(value ?? "").isValid;
}

/**
 * "<Status> <when>" for the read-only Reported status, so EPA's own resolution
 * status always carries the date it applies to.
 *
 * The event date (`violationEndDate`) is used for RESOLVED only. UNRESOLVED has
 * no determination date that means the same thing across programs — water's
 * violationStartDate is the monitoring period end, not the date EPA determined
 * anything — so dating it would be a wrong date that reads as working software.
 * UNKNOWN has none by definition. A date we can't parse leaves the sentence
 * rather than formatting as an em dash inside it.
 */
export function formatReportedStatus(
  status: HarmonizedViolationResolutionStatus,
  violationEndDate: string | null | undefined,
  lastSeenAt: string | null | undefined
): string {
  const label = prettyPrintEnumValue(status);
  const resolvedOn =
    status === HarmonizedViolationResolutionStatus.Resolved && isUsableDate(violationEndDate)
      ? ` on ${formatViolationDate(violationEndDate)}`
      : "";
  if (!isUsableDate(lastSeenAt)) return `${label}${resolvedOn}`;
  const confirmed = formatViolationDate(lastSeenAt);
  if (resolvedOn) return `${label}${resolvedOn} (confirmed ${confirmed})`;
  return `${label} as of ${confirmed}`;
}

export function formatDiscoveryQuarter(value: string | null | undefined): string {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  if (!dt.isValid) return "—";
  const quarter = Math.floor((dt.month - 1) / 3) + 1;
  return `${dt.year} Q${quarter}`;
}

export function formatDaysToResolution(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  if (!start || !end) return "—";
  const s = DateTime.fromISO(start);
  const e = DateTime.fromISO(end);
  if (!s.isValid || !e.isValid) return "—";
  return String(Math.round(e.diff(s, "days").days));
}

export function formatDiscoveryMonth(value: string | null | undefined): string {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toFormat("LLL yyyy") : "—";
}

const SEVERITY_COLOR: Record<HarmonizedViolationSeverity, "error" | "warning" | "info"> = {
  [HarmonizedViolationSeverity.High]: "error",
  [HarmonizedViolationSeverity.Medium]: "warning",
  [HarmonizedViolationSeverity.Low]: "info",
};

const STATUS_COLOR: Record<HarmonizedViolationResolutionStatus, "error" | "success" | "default"> = {
  [HarmonizedViolationResolutionStatus.Unresolved]: "error",
  [HarmonizedViolationResolutionStatus.Resolved]: "success",
  [HarmonizedViolationResolutionStatus.Unknown]: "default",
  [HarmonizedViolationResolutionStatus.NotReported]: "default",
};

export const NOT_REPORTED_EXPLANATION =
  "EPA does not report resolution outcomes for this violation type, so its status cannot be confirmed from EPA data.";

const OLD_SIGNAL_YEARS = 5;

export const OLD_SIGNAL_EXPLANATION =
  "This severity is based on a permit that has been expired or terminated for more than five years. If no action is required, set the Resolution status to Resolved.";

/**
 * True when a permit's terminal date (expiration/termination/retirement) is
 * old enough that the severity it drove is likely a stale signal rather than
 * something actionable today. `now` is a parameter (not `new Date()` inline)
 * so callers/tests stay deterministic.
 */
export function isOldPermitSignal(
  permitTerminalDate: string | null | undefined,
  now: Date
): boolean {
  if (permitTerminalDate == null) {
    return false;
  }
  const cutoff = new Date(now);
  cutoff.setFullYear(cutoff.getFullYear() - OLD_SIGNAL_YEARS);
  return new Date(permitTerminalDate) < cutoff;
}

// Each program gets a leading icon (decorative — the text label stays for
// accessibility) tinted with a theme palette color, so the program reads at a
// glance without adding another fully-colored chip next to severity/status.
// All @mui/icons-material icons share one component type, so `typeof Waves`
// types the whole set (and avoids a named barrel import that the
// use-explicit-icon-import ast-grep rule forbids).
const PROGRAM_DISPLAY: Record<
  HarmonizedViolationProgram,
  { Icon: typeof Waves; iconColor: string }
> = {
  [HarmonizedViolationProgram.Water]: { Icon: Waves, iconColor: "info.main" },
  [HarmonizedViolationProgram.Air]: { Icon: Air, iconColor: "text.secondary" },
  [HarmonizedViolationProgram.Waste]: { Icon: DeleteOutline, iconColor: "warning.dark" },
};

/**
 * Per-program hover copy, naming only the act the chip actually represents.
 * Hovering "Water" should say Clean Water Act — not recite every program.
 */
export const PROGRAM_TOOLTIP: Record<HarmonizedViolationProgram, string> = {
  [HarmonizedViolationProgram.Water]: "Clean Water Act — NPDES discharge violations.",
  [HarmonizedViolationProgram.Air]: "Clean Air Act violations.",
  [HarmonizedViolationProgram.Waste]:
    "Resource Conservation and Recovery Act (RCRA) — hazardous-waste violations.",
};

export function ProgramChip({ program }: { program: HarmonizedViolationProgram }) {
  const { Icon, iconColor } = PROGRAM_DISPLAY[program];
  return (
    <Tooltip describeChild title={PROGRAM_TOOLTIP[program]}>
      <Chip
        size="small"
        variant="outlined"
        icon={<Icon />}
        label={prettyPrintEnumValue(program)}
        // Nudge the icon right of the chip's left border so the whitespace on
        // either side of the icon reads symmetrically (default small-chip icon
        // marginLeft is a tight 4px).
        sx={{ "& .MuiChip-icon": { color: iconColor, marginLeft: "6px" } }}
      />
    </Tooltip>
  );
}

// Design-system "light-filled" chip variant (see .cursor/rules/design-system.mdc
// §Chips): the role's `main` color as the label, on an 8%-alpha fill, no border —
// a gentler badge than MUI's default solid colored chip. Colors only; size/text
// stay at MUI defaults per the spec.
function lightFilledChipSx(color: "info" | "success" | "error" | "warning") {
  return {
    color: `${color}.main`,
    bgcolor: (t: Theme) => alpha(t.palette[color].main, 0.08),
  };
}

/**
 * Per-level hover copy, explaining only the level the chip actually carries.
 * Hovering "High" should say what high means, not list every level.
 *
 * Severity is Encamp's own work-priority rating derived from EPA data — EPA has
 * no cross-program severity field — so each line says what Encamp keys on.
 * Keep in sync with SEVERITY_EXPLANATION below.
 */
export const SEVERITY_TOOLTIP: Record<HarmonizedViolationSeverity, string> = {
  [HarmonizedViolationSeverity.High]:
    "Encamp's highest priority. The facility is under an active EPA or state enforcement order, or EPA's data marks the violation as serious: any air violation, a waste handler EPA calls a significant non-complier, a water discharge in Reportable Non-Compliance, or a violation still open against an expired or terminated water permit.",
  [HarmonizedViolationSeverity.Medium]:
    "Encamp's middle priority. The hazardous-waste handler has unresolved violations, but EPA has not flagged it as a significant non-complier. Only waste violations are rated Medium.",
  [HarmonizedViolationSeverity.Low]:
    "Encamp's lowest priority. None of the High or Medium conditions apply — for example a waste handler with no unresolved violations, or a water violation with no Reportable Non-Compliance flag whose permit is still in force.",
};

export function SeverityChip({ severity }: { severity: HarmonizedViolationSeverity }) {
  return (
    <Tooltip describeChild title={SEVERITY_TOOLTIP[severity]}>
      <Chip
        size="small"
        label={prettyPrintEnumValue(severity)}
        sx={lightFilledChipSx(SEVERITY_COLOR[severity])}
      />
    </Tooltip>
  );
}

/**
 * Names the EPA Order that escalated a violation to High, in the violation
 * detail drawer alongside the severity chip. Only ever rendered when the
 * violation carries an `orderStatuteCode` (i.e. its severity determinant is
 * "ORDER"); the caller is responsible for that null check.
 *
 * `statuteCode` is rendered verbatim — today's values are RCRA/CWA/CAA, but
 * the set is expected to grow (CERCLA/SDWA are open SME questions), so this
 * must never switch on a fixed literal set.
 */
export function OrderEscalationChip({ statuteCode }: { statuteCode: string }) {
  return (
    <Chip
      size="small"
      label={`Escalated to High — facility is under an active ${statuteCode} order`}
      sx={lightFilledChipSx("error")}
    />
  );
}

/**
 * Names the state-issued formal enforcement order that escalated a violation
 * to High, alongside the severity chip in the detail drawer. Only ever
 * rendered when the violation's severity determinant is "STATE_ORDER"; the
 * caller is responsible for that check.
 */
export function StateOrderEscalationChip() {
  return (
    <Chip
      size="small"
      label="Escalated to High — violation is under a state-issued formal enforcement order"
      sx={lightFilledChipSx("error")}
    />
  );
}

export function StatusChip({
  status,
  tooltip,
}: {
  status: HarmonizedViolationResolutionStatus;
  /**
   * Optional hover explanation. Only the grid's Reported status column passes
   * one; the user-tracked Resolution status column renders the bare chip.
   */
  tooltip?: string;
}) {
  const color = STATUS_COLOR[status];
  const chip =
    color === "default" ? (
      // Unknown/Not Reported map to the spec's neutral grey: a plain outlined
      // chip (no color).
      <Chip size="small" variant="outlined" label={prettyPrintEnumValue(status)} />
    ) : (
      <Chip size="small" label={prettyPrintEnumValue(status)} sx={lightFilledChipSx(color)} />
    );

  // Not Reported always explains itself — it states a gap in EPA's feed that a
  // dated caller tooltip can't convey — so it outranks a passed-in tooltip.
  if (status === HarmonizedViolationResolutionStatus.NotReported) {
    return (
      <Tooltip describeChild title={NOT_REPORTED_EXPLANATION} arrow>
        {chip}
      </Tooltip>
    );
  }
  if (!tooltip) return chip;
  return (
    <Tooltip describeChild title={tooltip}>
      {chip}
    </Tooltip>
  );
}

// --- Metric explanations -------------------------------------------------
//
// Severity/Program/Stale are easy to misread (e.g. "why is everything High and
// Water, and why is Medium empty?"). These explanations are surfaced two ways:
// as a rich hover tooltip on the overview headings/cards (via MetricInfoIcon)
// and as the short plain-text header hint on the matching grid column. Keep the
// wording here so both surfaces stay in sync.

type MetricExplanation = {
  /** Lead sentence — the one-line takeaway. */
  summary: string;
  /** Optional per-value breakdown rendered as a bulleted list. */
  bullets?: { term: string; desc: string }[];
  /** Optional closing caveat (rendered italic). */
  caveat?: string;
};

export const SEVERITY_EXPLANATION: MetricExplanation = {
  summary:
    "Severity is Encamp's work-priority rating, derived from EPA data — EPA publishes no single severity field across its three programs, so we compute one.",
  bullets: [
    {
      term: "High",
      desc: "The facility is under an active formal EPA order under the matching statute, or a state has issued a formal enforcement order on the violation; any air violation (EPA reports these only as high-priority or federally reportable); a hazardous-waste (RCRA) handler EPA flags as a significant non-complier; a water (NPDES) discharge EPA flags as Reportable Non-Compliance; or a water violation still open against an expired, terminated, or retired permit.",
    },
    {
      term: "Medium",
      desc: "A hazardous-waste (RCRA) handler with unresolved violations that EPA has not flagged as a significant non-complier. Waste is the only program that produces Medium.",
    },
    {
      term: "Low",
      desc: "Nothing above applies — e.g. a handler with no unresolved RCRA violations, or a water violation with no Reportable Non-Compliance flag whose permit is still in force.",
    },
  ],
  caveat:
    "An order whose settlement has been entered no longer raises severity — it stays on the facility's permanent record, but it is no longer an open matter. So a violation can show an order in ECHO and still not be High. Severity ranks work, it does not measure harm.",
};

export const PROGRAM_EXPLANATION: MetricExplanation = {
  summary: "Violations grouped by EPA media program.",
  bullets: [
    { term: "Water", desc: "NPDES (Clean Water Act) discharge violations." },
    { term: "Air", desc: "Clean Air Act violations." },
    { term: "Waste", desc: "RCRA hazardous-waste violations." },
  ],
  caveat: "Waste appears only after RCRA data has been imported, so it may be empty until then.",
};

export const STALE_EXPLANATION: MetricExplanation = {
  summary: "“Stale” means our latest EPA data refresh no longer includes this violation.",
  caveat:
    "The last refresh that still found it is older than our latest one, so it has dropped off EPA's published data — often because it was resolved or superseded upstream, though Encamp hasn't independently confirmed that.",
};

/**
 * Flat-string form of STALE_EXPLANATION for the chip's own hover tooltip. The
 * chip is the hover target (no separate info icon), and a plain Tooltip takes a
 * string, so the structured explanation is flattened rather than rendered.
 */
export const STALE_CHIP_TOOLTIP =
  `${STALE_EXPLANATION.summary} ${STALE_EXPLANATION.caveat ?? ""}`.trim();

/**
 * "Stale" marker for a violation EPA has stopped reporting. Shared by the grid's
 * Last seen cell and the drawer's chip row so the two can't drift.
 */
export function StaleChip() {
  return (
    <Tooltip describeChild title={STALE_CHIP_TOOLTIP}>
      <Chip size="small" color="warning" variant="outlined" label="Stale" />
    </Tooltip>
  );
}

/** Short single-sentence hints for the grid column-header tooltips. */
export const SEVERITY_HEADER_HINT =
  "Encamp's work-priority rating, derived from EPA data (not a harm score): High = an active EPA or state enforcement order, any air violation, an RCRA significant non-complier, water Reportable Non-Compliance, or a violation open against a dead water permit; Medium = an RCRA handler with unresolved violations; Low = everything else.";
export const PROGRAM_HEADER_HINT =
  "EPA media program. Waste (RCRA) violations appear only after RCRA data has been imported.";
// --- Reported status ------------------------------------------------------
//
// Plain strings rather than a MetricExplanation: the drawer surfaces this
// through InfoIconTooltip -> BigTooltip, which renders `\n\n`-delimited text
// under `white-space: pre-wrap` (no bullets, click-to-toggle), matching the
// waste GeneratorStatusTile. Composed with `+` rather than line continuations so
// no indentation leaks into the visible paragraphs.
const REPORTED_STATUS_LAG =
  "EPA data is refreshed weekly and can lag several weeks behind the agency's own records.";

export const REPORTED_STATUS_TOOLTIP =
  "Resolution status exactly as the source government data reports it. Read-only — " +
  "track your own position with the Resolution status field above." +
  "\n\nFor resolved violations we show EPA's own resolution date, then the date we last " +
  "confirmed the status was still current. Otherwise we show only the confirmation date, " +
  "because EPA publishes no reliable determination date across all three programs." +
  "\n\n" +
  REPORTED_STATUS_LAG;

/**
 * One-line version of the above for the grid's Reported status column header.
 * The per-row chip tooltip is the `formatReportedStatus` sentence, not this —
 * this explains what the column is, that one says what the row says.
 */
export const REPORTED_STATUS_HEADER_HINT =
  "Resolution status as reported in the source government data (EPA/state). Read-only — " +
  "dated with EPA's resolution date when resolved, otherwise with the date we last confirmed " +
  "the status. Track your own resolution in the 'Resolution status' column.";

/**
 * Hover copy for the page's top-right "Violations data updated …" label. The
 * label states when OUR refresh last completed; this says what that does and
 * does not tell you about EPA's own records.
 */
export const DATA_FRESHNESS_TOOLTIP =
  "When Encamp last completed a refresh of EPA violation data. " + REPORTED_STATUS_LAG;

export const STALE_HEADER_HINT =
  "Last time one of our EPA data refreshes still found this violation — our own date, not EPA's. “Stale” means it predates our latest refresh, so EPA no longer lists it.";

function ExplanationBody({ explanation }: { explanation: MetricExplanation }) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" component="p">
        {explanation.summary}
      </Typography>
      {explanation.bullets?.length ? (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
          {explanation.bullets.map((b) => (
            <Typography key={b.term} variant="caption" component="li">
              <strong>{b.term}</strong> — {b.desc}
            </Typography>
          ))}
        </Box>
      ) : null}
      {explanation.caveat ? (
        <Typography variant="caption" component="p" sx={{ mt: 0.5, fontStyle: "italic" }}>
          {explanation.caveat}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * A small info icon that reveals a metric explanation on CLICK, in an opaque
 * card. Delegates to the shared InfoIconTooltip (-> BigTooltip), which is the
 * established pattern on the waste facility page — these explanations are long
 * enough that a hover tooltip vanished while being read.
 *
 * `label` names the metric for screen readers (e.g. "severity").
 */
export function MetricInfoIcon({
  explanation,
  label,
}: {
  explanation: MetricExplanation;
  label: string;
}) {
  return (
    <InfoIconTooltip
      tooltip={<ExplanationBody explanation={explanation} />}
      ariaLabel={`About ${label}`}
      iconButtonSx={{ p: 0.25 }}
    />
  );
}

/** A small uppercase section label dividing the drawer into Tracking / Details. */
export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Typography variant="overline" color="text.primary" sx={{ mt: 1, fontWeight: 700 }}>
      {children}
    </Typography>
  );
}
